import videojs from 'video.js';

let localize;
const _logBox = new WeakMap();

/** 
 * This plugin is similar to the logging plugin, but it is specifically designed to log control bar actions
 * out loud for screen readers.
**/

class ControlsLog extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const Component = videojs.getComponent('Component');
        const logComponent = new Component(player, { name: 'ControlsLog' });

        logComponent.addClass('mediasite-player__controls-log');
        logComponent.addClass('sr-only');
        const logBox = logComponent.el();
        logBox.setAttribute('role', 'log');
        logBox.setAttribute('aria-live', 'assertive');
        logBox.setAttribute('tabindex', -1);
        _logBox.set(this, logBox);

        player.ready(() => {
            player.layout().addChild(logComponent);
            this.setUpListeners();
        });

        this.player = player;
    }

    // Calls out the effect of a user's interaction with the control bar.
    logAction(action, localizeMsg) {
        const logBox = _logBox.get(this);
        let currentText = '';
        logBox.childNodes.forEach((kiddo) => {
            currentText = kiddo.textContent;
            logBox.removeChild(kiddo);
        });

        // VoiceOver on macOS does not announce the last line of a log if it is the same as the previous one,
        //  so we add or remove a period to ensure it's nominally different from the last command.
        if (currentText === action) {
            if (action.endsWith('.')) {
                action = action.slice(0, -1);
            } else {
                action += '.';
            }
        }

        // Add a new line to the screen reader "log"
        const logLine = document.createElement('p');
        logBox.appendChild(logLine);
        const logMessage = localizeMsg ? localize(action) : action;
        logLine.textContent = logMessage;
    }

    // Special cases for play/pause, fullscreen, and mute toggle buttons.
    setUpListeners() {
        const controlBar = this.player.getChild('ControlBar');
        const playButton = controlBar.getChild('PlayToggle');
        const fullscreenButton = controlBar.getChild('FullscreenToggle');
        const muteToggle = controlBar.getChild('VolumePanel')?.getChild('MuteToggle');
        const pipToggle = controlBar.getChild('PictureInPictureToggle');

        [playButton, fullscreenButton, muteToggle].forEach((button) => {
            button?.el().querySelector('.vjs-control-text')?.removeAttribute('aria-live');
        });

        if (playButton) {
            this.player.on(playButton, 'click', () => {
                const message = playButton.hasClass('vjs-paused') ? 'Playing' : 'Paused';
                this.logAction(message, true);
            });
        }

        if (fullscreenButton) {
            this.player.on(fullscreenButton, 'click', () => {
                this.logAction('Fullscreen toggled', true);
            });
        }

        if (muteToggle) {
            this.player.on(muteToggle, 'click', () => {
                const message = muteToggle.hasClass('vjs-vol-0') ? 'Volume Unmuted' : 'Volume Muted';
                // VoiceOver hack to prevent button title from announcing before the log message.
                setTimeout(() => {
                    this.logAction(message, true);
                }, 1);
            });
        }

        if (pipToggle) {
            this.player.on(pipToggle, 'click', () => {
                this.logAction('Picture-in-picture toggled', true);
            });
        }
    }
}

export default ControlsLog;